import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../utils/api';
import { useAuth } from '../utils/AuthContext';
import { Plus, Trash2, X, Loader2, AlertCircle, Layers, Search } from 'lucide-react';

const PATTERN_TYPES = [
  'Defensive', 'Attacking', 'Transition', 'Set piece', 'Pressing',
  'Build-up', 'Game management', 'Player role', 'Substitution', 'Other'
];
const SEVERITY_OPTIONS = ['Low', 'Medium', 'High'];
const STATUS_OPTIONS = ['active', 'improving', 'resolved', 'watchlist'];

const severityBadge = (sev) => {
  const map = { High: 'bg-red-100 text-red-700', Medium: 'bg-amber-100 text-amber-700', Low: 'bg-emerald-100 text-emerald-700' };
  return map[sev] || 'bg-gray-100 text-gray-700';
};
const statusBadge = (st) => {
  const map = {
    active: 'bg-red-100 text-red-700',
    improving: 'bg-amber-100 text-amber-700',
    resolved: 'bg-emerald-100 text-emerald-700',
    watchlist: 'bg-blue-100 text-blue-700'
  };
  return map[st] || 'bg-gray-100 text-gray-700';
};

function inputCls() {
  return 'w-full bg-white border border-gray-300 rounded-lg px-4 py-2.5 text-navy-900 placeholder-gray-400 focus:outline-none focus:border-navy-600';
}
function labelCls() {
  return 'text-gray-500 text-xs mb-1 block font-medium';
}

const EMPTY_FORM = {
  pattern_name: '', pattern_type: 'Defensive', team_id: '',
  first_detected: '', last_detected: '', occurrence_count: 1,
  severity: 'Medium', status: 'active', recommended_training: '',
  related_reports: '', notes: ''
};

export default function Patterns() {
  const { user } = useAuth();
  const canWrite = String(user?.role || '').toLowerCase() !== 'viewer';
  const [patterns, setPatterns] = useState([]);
  const [teams, setTeams] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    api.getTeams().then((data) => setTeams(data.teams || [])).catch(() => {});
  }, []);

  const loadPatterns = useCallback(() => {
    setLoading(true);
    setError('');
    api.getPatterns(selectedTeam || undefined)
      .then((data) => setPatterns(data.patterns || []))
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [selectedTeam]);

  useEffect(() => { loadPatterns(); }, [loadPatterns]);

  const filtered = searchQuery.trim()
    ? patterns.filter((p) =>
        (p.pattern_name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (p.pattern_type || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (p.notes || '').toLowerCase().includes(searchQuery.toLowerCase()))
    : patterns;

  const openNew = () => {
    setForm({ ...EMPTY_FORM, team_id: selectedTeam });
    setEditingId(null);
    setShowForm(true);
  };

  const openEdit = (p) => {
    setForm({
      pattern_name: p.pattern_name || '',
      pattern_type: p.pattern_type || 'Defensive',
      team_id: p.team_id || '',
      first_detected: p.first_detected || '',
      last_detected: p.last_detected || '',
      occurrence_count: p.occurrence_count || 1,
      severity: p.severity || 'Medium',
      status: p.status || 'active',
      recommended_training: p.recommended_training || '',
      related_reports: Array.isArray(p.related_reports) ? p.related_reports.join(', ') : (p.related_reports || ''),
      notes: p.notes || ''
    });
    setEditingId(p.id);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.pattern_name.trim() || !form.pattern_type.trim()) {
      setError('Pattern name and type are required');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload = {
        ...form,
        occurrence_count: parseInt(form.occurrence_count) || 1,
        related_reports: form.related_reports
          ? form.related_reports.split(',').map((s) => s.trim()).filter(Boolean)
          : null
      };
      if (editingId) {
        payload.id = editingId;
        await api.updatePattern(payload);
      } else {
        await api.createPattern(payload);
      }
      setShowForm(false);
      loadPatterns();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this pattern?')) return;
    try {
      await api.deletePattern(id);
      loadPatterns();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-navy-900 flex items-center gap-2">
            <Layers className="w-7 h-7 text-navy-600" /> Pattern Library
          </h1>
          <p className="text-gray-500 text-sm mt-1">Recurring tactical patterns detected and tracked across your team's reports.</p>
        </div>
        {canWrite && (
          <button onClick={openNew} className="flex items-center gap-2 bg-navy-900 hover:bg-navy-800 text-white font-medium px-5 py-2.5 rounded-xl transition-colors">
            <Plus className="w-5 h-5" /> New Pattern
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <select className="bg-white border border-gray-300 rounded-lg px-4 py-2.5 text-navy-900 focus:outline-none focus:border-navy-600" value={selectedTeam} onChange={(e) => setSelectedTeam(e.target.value)}>
          <option value="">All teams</option>
          {teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input className="w-full bg-white border border-gray-300 rounded-lg pl-10 pr-4 py-2.5 text-navy-900 placeholder-gray-400 focus:outline-none focus:border-navy-600" placeholder="Search patterns..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 mb-4 flex items-center gap-2 text-red-700 text-sm">
          <AlertCircle className="w-4 h-4 flex-shrink-0" /> {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 text-navy-400 animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-gray-300 rounded-xl">
          <Layers className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 font-medium">No patterns yet</p>
          <p className="text-gray-400 text-sm mt-1">Patterns are auto-detected by the analysis engine, or you can add one manually.</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {filtered.map((p) => (
            <div key={p.id} className="border border-gray-200 rounded-xl p-5 bg-white hover:shadow-md transition-shadow">
              <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-2">
                    <h3 className="text-lg font-semibold text-navy-900">{p.pattern_name}</h3>
                    <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-navy-100 text-navy-700">{p.pattern_type}</span>
                    <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">{p.occurrence_count}× seen</span>
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${severityBadge(p.severity)}`}>{p.severity}</span>
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${statusBadge(p.status)}`}>{p.status}</span>
                  </div>
                  {(p.first_detected || p.last_detected) && (
                    <p className="text-gray-400 text-xs mb-2">
                      {p.first_detected && `First: ${p.first_detected}`}
                      {p.first_detected && p.last_detected && ' · '}
                      {p.last_detected && `Last: ${p.last_detected}`}
                    </p>
                  )}
                  {p.recommended_training && (
                    <div className="mb-2">
                      <span className="text-gray-500 text-xs font-semibold uppercase tracking-wide">Recommended Training</span>
                      <p className="text-gray-700 text-sm mt-0.5">{p.recommended_training}</p>
                    </div>
                  )}
                  {Array.isArray(p.related_reports) && p.related_reports.length > 0 && (
                    <div className="mb-2">
                      <span className="text-gray-500 text-xs font-semibold uppercase tracking-wide">Related Reports</span>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {p.related_reports.map((rid, idx) => (
                          <Link key={idx} to={`/analysis/${rid}`} className="text-navy-600 hover:text-navy-900 text-sm font-medium underline">
                            View report
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                  {p.notes && <p className="text-gray-600 text-sm mt-2">{p.notes}</p>}
                </div>
                {canWrite && (
                  <div className="flex gap-2 flex-shrink-0">
                    <button onClick={() => openEdit(p)} className="text-navy-600 hover:text-navy-900 text-sm font-medium px-3 py-1.5 rounded-lg hover:bg-navy-50 transition-colors">Edit</button>
                    <button onClick={() => handleDelete(p.id)} className="text-gray-400 hover:text-red-600 p-1.5"><Trash2 className="w-4 h-4" /></button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal Form */}
      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-navy-900">{editingId ? 'Edit Pattern' : 'New Pattern'}</h2>
              <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className={labelCls()}>Pattern Name *</label>
                <input className={inputCls()} value={form.pattern_name} onChange={(e) => setForm({ ...form, pattern_name: e.target.value })} placeholder="e.g. Right-side overload vulnerability" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls()}>Type *</label>
                  <select className={inputCls()} value={form.pattern_type} onChange={(e) => setForm({ ...form, pattern_type: e.target.value })}>
                    {PATTERN_TYPES.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls()}>Team</label>
                  <select className={inputCls()} value={form.team_id} onChange={(e) => setForm({ ...form, team_id: e.target.value })}>
                    <option value="">None / All teams</option>
                    {teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls()}>Severity</label>
                  <select className={inputCls()} value={form.severity} onChange={(e) => setForm({ ...form, severity: e.target.value })}>
                    {SEVERITY_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls()}>Status</label>
                  <select className={inputCls()} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                    {STATUS_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className={labelCls()}>Occurrences</label>
                  <input type="number" min="1" className={inputCls()} value={form.occurrence_count} onChange={(e) => setForm({ ...form, occurrence_count: e.target.value })} />
                </div>
                <div>
                  <label className={labelCls()}>First Detected</label>
                  <input className={inputCls()} value={form.first_detected} onChange={(e) => setForm({ ...form, first_detected: e.target.value })} placeholder="2026-07-01" />
                </div>
                <div>
                  <label className={labelCls()}>Last Detected</label>
                  <input className={inputCls()} value={form.last_detected} onChange={(e) => setForm({ ...form, last_detected: e.target.value })} placeholder="2026-07-09" />
                </div>
              </div>
              <div>
                <label className={labelCls()}>Recommended Training</label>
                <input className={inputCls()} value={form.recommended_training} onChange={(e) => setForm({ ...form, recommended_training: e.target.value })} placeholder="e.g. Defensive transition drills" />
              </div>
              <div>
                <label className={labelCls()}>Related Report IDs (comma-separated)</label>
                <input className={inputCls()} value={form.related_reports} onChange={(e) => setForm({ ...form, related_reports: e.target.value })} placeholder="e.g. report-uuid-1, report-uuid-2" />
              </div>
              <div>
                <label className={labelCls()}>Notes</label>
                <textarea className={inputCls() + ' resize-none'} rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Additional context about this pattern..." />
              </div>
            </div>
            <div className="flex gap-3 mt-5">
              <button onClick={() => setShowForm(false)} className="flex-1 border border-gray-300 text-gray-600 font-medium px-4 py-2.5 rounded-xl hover:bg-gray-50 transition-colors">Cancel</button>
              <button onClick={handleSave} disabled={saving} className="flex-1 bg-navy-900 hover:bg-navy-800 disabled:opacity-50 text-white font-medium px-4 py-2.5 rounded-xl transition-colors flex items-center justify-center gap-2">
                {saving && <Loader2 className="w-4 h-4 animate-spin" />} {editingId ? 'Save' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
